import { createContext, useCallback, useContext, useMemo, useRef, useSyncExternalStore } from 'react';
import { NO_VALUE, duration } from '../format.ts';
import { useNow } from '../useNow.ts';
import { faultOf, nextAttemptInMs, type Fault } from './fault.ts';
import { useSources, type Source } from './sources.ts';

/* ==========================================================================
   One read, as the chrome needs to describe it.

   TanStack gives a surface six independent booleans; an operator needs one
   word. The mapping is the whole point of this file, and it has exactly four
   answers, because those are the four different pictures:

     first     nothing has ever landed and a read is in flight  → skeleton
     live      figures on screen, reads landing                 → the data
     stalled   figures on screen, the last read failed          → data + fault strip
     blank     nothing on screen, the last read failed          → fault block

   `stalled` is the one that matters and the one consoles skip. It is the
   state where the screen still looks like an instrument, every number on it
   is real, and not one of them is current. It must never look like `live`,
   and it must never be flattened into `blank` — throwing away a fleet the
   operator was reading because the newest poll timed out is worse than the
   outage.
   ========================================================================== */

export type Phase = 'first' | 'live' | 'stalled' | 'blank';

/** The subset of `UseQueryResult` this needs. Any TanStack query satisfies it. */
export interface QueryLike {
  data: unknown;
  dataUpdatedAt: number;
  errorUpdatedAt: number;
  error: unknown;
  isFetching: boolean;
  failureCount: number;
  refetch: () => unknown;
}

export interface ReadOptions {
  /** The endpoint, printed verbatim on the fault line. `/api/robots`. */
  path?: string | undefined;
  /** Declared cadence in ms. Omit where the source is read on demand. */
  everyMs?: number | undefined;
  /** The noun the fault sentence names. Defaults to `path`. */
  subject?: string | undefined;
  /** The read landed and carried nothing. Drives the empty line, not a fault. */
  empty?: boolean | undefined;
}

export interface Read {
  phase: Phase;
  /** A request is in flight right now. Independent of phase. */
  fetching: boolean;
  hasData: boolean;
  /** The read landed and there was nothing in it. */
  empty: boolean;
  /** Age of the newest good read, ms. Null = nothing has ever landed. */
  ageMs: number | null;
  /**
   * HOW LONG THE READ IN FLIGHT HAS BEEN IN FLIGHT, ms. Null when nothing is.
   *
   * `ageMs` answers "how old is what I am looking at" and is null for exactly
   * the state this answers: a panel that has never landed. Without it the
   * console's in-flight state is the one state on the surface with no clock on
   * it — `Reading…` reads the same at 300 ms and at four minutes — while the
   * banner two rows up has been counting an outage in seconds since the first
   * round. A claim of work in progress owes the same evidence a claim of
   * failure does.
   *
   * Measured from the moment this read last went from settled to fetching, so
   * it is the age of THIS attempt: a poll that has been running 40 s is a
   * different fact from a source that has been polling for 40 s.
   */
  waitingMs: number | null;
  lastGoodIso: string | null;
  fault: Fault | null;
  /** Consecutive failures since the last good read. */
  attempts: number;
  /** ms until the console next tries on its own. Null = it will not. */
  nextInMs: number | null;
  path: string | null;
  everyMs: number | null;
  subject: string;
  /** Re-read now. */
  retry: () => void;
}

export function useRead(query: QueryLike, options: ReadOptions = {}): Read {
  const { path, everyMs, subject, empty } = options;
  const { data, dataUpdatedAt, errorUpdatedAt, error, isFetching, failureCount } = query;
  const now = useNow(true);
  const { refetch } = query;

  /* THE FLICKER THIS REF EXISTS TO KILL.

     TanStack clears `error` and drops `status` back to `pending` the instant a
     new fetch begins on a query that has never held data (query-core's
     `fetchState`: `...data === undefined && { error: null, status: 'pending' }`).
     So a panel that has never landed, on a five-second poll, against an API
     that is down, alternates every five seconds between

       blank   the fault bar, the endpoint, the run length, the countdown
       first   a bright skeleton and the word "Reading"

     — which is the console forgetting an outage twice a minute and offering
     the operator hope on a schedule. The last committed fault is remembered
     here until a read actually succeeds, so a source that has failed stays
     failed on screen while it retries, and the retry says so in the bar's own
     `reading…` rather than by wiping the diagnosis off the panel. */
  const held = useRef<{ fault: Fault; runs: number } | null>(null);

  /* WHEN THIS ATTEMPT STARTED. TanStack stamps the two settled outcomes and
     nothing in between, so the only timestamp the loading state needs is the
     one the query does not keep. Written on the render that first sees a fetch
     in flight and cleared the moment it settles, which makes it the age of the
     current attempt rather than of the source. Same render-phase idiom as
     `held` above and idempotent for the same reason: it only ever writes a
     slot it found empty. */
  const started = useRef<number | null>(null);
  if (isFetching) started.current ??= Date.now();
  else started.current = null;
  const live = faultOf(error);
  if (live !== null) held.current = { fault: live, runs: Math.max(1, failureCount) };
  else if (data !== undefined) held.current = null;
  const kept = data === undefined ? held.current : null;

  const begun = started.current;

  return useMemo(() => {
    const fault = live ?? kept?.fault ?? null;
    const attempts = live !== null ? failureCount : (kept?.runs ?? failureCount);
    const hasData = data !== undefined;
    const phase: Phase = hasData
      ? fault === null
        ? 'live'
        : 'stalled'
      : fault === null
        ? 'first'
        : 'blank';

    const ageMs = dataUpdatedAt > 0 ? Math.max(0, now - dataUpdatedAt) : null;
    const waitingMs = begun === null ? null : Math.max(0, now - begun);

    /* Mid-retry there is no committed failure stamp to count from, and the
       panel is already saying it is reading — so a countdown is only offered
       once the read has settled into its error. */
    const nextInMs =
      fault === null || isFetching || errorUpdatedAt === 0
        ? null
        : nextAttemptInMs(fault, errorUpdatedAt, attempts, everyMs ?? null, now);

    return {
      phase,
      fetching: isFetching,
      hasData,
      empty: hasData && empty === true,
      ageMs,
      waitingMs,
      lastGoodIso: dataUpdatedAt > 0 ? new Date(dataUpdatedAt).toISOString() : null,
      fault,
      attempts,
      nextInMs,
      path: path ?? null,
      everyMs: everyMs ?? null,
      subject: subject ?? path ?? 'this source',
      retry: () => void refetch(),
    };
  }, [
    data, dataUpdatedAt, errorUpdatedAt, live, kept, isFetching, failureCount,
    begun, now, path, everyMs, subject, empty, refetch,
  ]);
}

/* --------------------------------------------------------------------------
   Is it this endpoint, or is it the link?

   The single most useful thing a console can tell an operator during an
   incident, and it cannot be answered from one query: `/api/scans` returning
   503 while `/api/robots` returns 200 is a broken handler, and the operator
   should carry on working. The same 503 with nothing else landing is an
   outage, and they should stop trusting the screen.

   So the answer is read off the whole cache, once a second, from the queries
   that are already mounted. No second registry, no extra subscription — the
   truth is already in the query cache and duplicating it is how two parts of
   a console end up disagreeing about whether it is connected.
   -------------------------------------------------------------------------- */

export interface Outage {
  /** Mounted reads whose last attempt failed at the transport layer. */
  down: number;
  /** Mounted reads the API answered, with a failure. */
  refused: number;
  /**
   * Mounted reads whose good result is still EVIDENCE THAT THE LINK IS UP —
   * see `supplying`. Not simply "queries whose last result was a success":
   * a source read once at page load and never polled again stays `success`
   * for the rest of the session, and counting it kept a console whose every
   * live read had stopped calling itself a partial outage.
   */
  answering: number;
  /**
   * Nothing is getting through: at least one source is failing at the
   * transport layer and not one source is answering. This is the difference
   * between "that panel is broken" and "you are disconnected".
   */
  disconnected: boolean;
  /**
   * Every mounted read is failing — by transport OR because the API is
   * answering with a fault. A console-level condition, so panels stop
   * repeating it one at a time and one loud line upstairs carries it.
   */
  systemic: boolean;
  /** ms since the newest good read anywhere. Null = nothing, this session. */
  sinceMs: number | null;
}

/* A SUCCESSFUL READ IS EVIDENCE THE LINK IS UP ONLY IF IT HAPPENED AFTER THE
   TROUBLE STARTED.

   This is the whole difference between "that endpoint is broken" and "you are
   disconnected", and getting it wrong is how a console reassures an operator
   during an outage. `/api/morphologies` and `/api/auth/me` are read ONCE when
   a surface mounts and never polled again; four minutes into a dead site link
   they are still sitting in the cache with `status: 'success'`. Counting them
   made /fleet/list report `PARTIAL OUTAGE · 4 of 7 sources have stopped
   answering` over a screen on which nothing whatsoever was arriving, for the
   whole duration of the outage.

   So: the outage began, at the latest, when the most recently-good failing
   source last answered — anything more recent than that proves the link was up
   after the trouble started, and anything older proves nothing at all. A fixed
   grace window cannot do this job, because the cadences on this console run
   from 3 s to read-once and a source polled every five minutes says nothing
   about the last thirty seconds. */
function beganMs(sources: readonly Source[]): number | null {
  let began: number | null = null;
  for (const s of sources) {
    if (!s.supply || s.ageMs === null) continue;
    began = began === null ? s.ageMs : Math.min(began, s.ageMs);
  }
  return began;
}

function supplying(s: Source, began: number | null): boolean {
  if (s.state !== 'answering' || s.ageMs === null) return false;
  /* Nothing failing has ever landed, so there is no start to measure from:
     fall back to plain recency, generously. */
  return began === null ? s.ageMs <= 30_000 : s.ageMs < began;
}

export function useOutage(): Outage {
  const sources = useSources();
  const now = useNow(true);

  return useMemo(() => {
    let down = 0;
    let refused = 0;
    let answering = 0;
    let newest = 0;
    const began = beganMs(sources);

    for (const s of sources) {
      if (s.lastGoodIso !== null) {
        const t = new Date(s.lastGoodIso).getTime();
        if (t > newest) newest = t;
      }
      /* A 404 or a 403 is a complete, final answer from a healthy API: the
         console is being supplied perfectly and one object is not there.
         Counting it as an outage is how a deregistered robot makes a console
         announce that it has lost the fleet. */
      if (s.supply) {
        if (s.down) down += 1;
        else refused += 1;
      } else if (supplying(s, began)) {
        answering += 1;
      }
    }

    const failing = down + refused;
    return {
      down,
      refused,
      answering,
      disconnected: down > 0 && answering === 0,
      systemic: failing > 1 && answering === 0,
      sinceMs: newest > 0 ? Math.max(0, now - newest) : null,
    };
  }, [sources, now]);
}

/* --------------------------------------------------------------------------
   THE FRESHNESS VERDICT, AS ONE WORD, FOR ANYTHING THAT CLAIMS TO BE LIVE.

   `Surface` writes this on the route as `data-link`, which demotes every
   liveness hue inside it (see `surface.module.css`) — that reaches the COLOUR
   of a `LIVE` pill, a `● Active` dot or a green vitals cell without the
   surface owning any of them. What a custom property cannot reach is the
   WORD: `Live` has to become `Live off`, `newest` has to become `2m 14s old`,
   and `100% covered · 0 holes` has to stop counting a tail nobody read.

   So the same verdict is available as a value. Any surface that prints a
   liveness word asks this hook and words itself:

     const { stopped, silentMs } = useLink();
     <Pill tone={stopped ? 'offline' : 'live'}>
       {stopped ? 'Live off' : 'Live'}
     </Pill>

   One definition, so the pill and the palette can never disagree.
   -------------------------------------------------------------------------- */

/* --------------------------------------------------------------------------
   IS THIS MACHINE ON A NETWORK AT ALL?

   `navigator.onLine` is not allowed to decide BEHAVIOUR in this console —
   `queryClient.ts` sets `networkMode: 'always'` precisely because the flag is
   wrong in both directions: a laptop cabled to an isolated robot network
   reports offline with the API a metre away, and a laptop on café wi-fi
   reports online with the VPN to the API down. Every read is attempted, and
   the console reports what the attempt actually did.

   It is allowed to decide WORDS. When every read is failing at the transport
   layer, the operator's next action forks on exactly this fact and nothing
   else on the screen can supply it:

     off the network   fix this machine's link. The API is irrelevant until
                       then, and telling someone to call the network team
                       about a dropped wi-fi association wastes the first
                       minute of an incident.
     on the network    the machine is fine and the API, the VPN or the site
                       link is not. That is somebody else's pager.

   Never on its own, though: the flag is a HINT and it only earns a word when a
   real request has actually failed at the transport layer (see the banner).
   An event subscription rather than a poll, so it is exact the instant the OS
   changes its mind rather than up to a second late.
   -------------------------------------------------------------------------- */

export function useOnline(): boolean {
  const subscribe = useCallback((fire: () => void) => {
    window.addEventListener('online', fire);
    window.addEventListener('offline', fire);
    return () => {
      window.removeEventListener('online', fire);
      window.removeEventListener('offline', fire);
    };
  }, []);
  return useSyncExternalStore(
    subscribe,
    () => (typeof navigator === 'undefined' ? true : navigator.onLine),
    () => true,
  );
}

export type LinkState = 'up' | 'partial' | 'refused' | 'down';

export interface Link {
  /** The word. Identical to the `data-link` attribute `Surface` publishes. */
  state: LinkState;
  /**
   * NOT ONE FIGURE ON THIS SURFACE IS CURRENT — `down` (nothing is arriving)
   * or `refused` (the API answers every read with a fault). `partial` is
   * false: some sources are answering and those panels are telling the truth.
   */
  stopped: boolean;
  /** ms since the newest good read anywhere. Null = nothing, this session. */
  silentMs: number | null;
}

export function useLink(): Outage & Link {
  const outage = useOutage();
  const sources = useSources();
  const faulted = sources.some((s) => s.supply);

  const state: LinkState = !faulted
    ? 'up'
    : outage.disconnected
      ? 'down'
      : outage.systemic
        ? 'refused'
        : 'partial';

  return {
    ...outage,
    state,
    stopped: state === 'down' || state === 'refused',
    silentMs: outage.sinceMs,
  };
}

/* --------------------------------------------------------------------------
   THE VERDICT, PUBLISHED DOWNWARD SO NO PANEL HAS TO RE-DERIVE IT.

   `useLink` is the expensive answer: it walks the query cache and it ticks at
   1 Hz, because the banner prints a countdown. A PANEL does not need the
   countdown — it needs one boolean, "has the surface already said this?" —
   and eight panels each mounting a 1 Hz subscription to learn it would repaint
   the whole route once a second for the duration of an outage, which is both
   a performance cost and, under THE BAR §7, a screen where something moves for
   no reason.

   So `Surface` computes the verdict once and publishes the WORD. It changes
   only when the diagnosis changes, so a panel re-renders on the transition and
   at no other time.
   -------------------------------------------------------------------------- */

export const SurfaceLink = createContext<LinkState>('up');

/**
 * The surface's own verdict, as published by `Surface`. `up` outside one —
 * a panel with no surface above it has no banner above it either, so it
 * states its own faults, which is the safe default.
 */
export function useSurfaceLink(): LinkState {
  return useContext(SurfaceLink);
}

/**
 * TRUE WHEN THE BANNER HAS ALREADY SAID IT.
 *
 * `down` and `refused` both mean NOT ONE FIGURE ON THIS SURFACE IS CURRENT,
 * and in both the banner at the top of the route names the endpoints, the
 * silence and the next attempt. A panel that repeats the endpoint underneath
 * is the third restatement of one fact: measured on one 1600×1000 `/fleet`
 * frame, `no reply` ×17, `Unreachable` ×7, `/api/robots` ×9 — 41 restatements
 * costing eight panels ~44px each.
 *
 * `partial` is the opposite case and the reason this is not simply "is
 * anything failing": there the banner cannot name the panel, because the rest
 * of the surface is answering and the panel is the only place that knows
 * which source went. `/api/robots/rover-01/telemetry` dying under a healthy
 * console is exactly the fault an operator must be able to attribute.
 */
export function useBannerCarries(): boolean {
  const state = useContext(SurfaceLink);
  return state === 'down' || state === 'refused';
}

/* ==========================================================================
   A ZERO IS A MEASUREMENT. AN UNREAD SOURCE IS NOT.

   Every other shape in this module answers the question "what does a panel
   draw when it has nothing". These two answer the question that outlives it:
   what does a NUMBER print when the thing it was counted from never arrived.

   The failure is always the same three characters of JavaScript. A surface
   holds `const rows = query.data ?? []`, and from that moment every figure it
   derives is a confident lie the moment the endpoint stops answering:

     Robots  0 registered              …the roster did not answer
     0 rows · 0 live · 0 faulted       …none of these was counted
     Queue (0) · 0 OPEN · 0 FAILED     …the ledger did not answer
     DATA PRESENCE 0% covered 0 holes  …nothing was read to cover anything
     rover-01 has never reported       …rover-01 has 150 readings; the READ
                                        failed, and the panel diagnosed the
                                        ROBOT for it

   Each of those is worse than a blank, because a blank is obviously missing
   and a zero is obviously *fine*. An operator scanning a wall display reads
   `0 faulted` as good news. The console has to be incapable of saying it.

   ONE SOURCE OR SEVERAL. A figure counted across two endpoints is unread only
   when NEITHER answered: a fleet total that has the roster but not the stats
   aggregate is still a real total, and blanking it would throw away a reading
   the console holds. So `of` takes an array and the test is "not one of these
   has landed".

   WHY NOT `useSupply().starved`. `Blank` asks that question — is the whole
   console starved — and it is the right question for a SENTENCE, which is
   about the surface. It is the wrong question for a FIGURE, which is about
   one source: `/api/robots/rover-01/telemetry` can be the only dead endpoint
   on a perfectly healthy console, and the Bring-up ladder that reads it must
   still refuse to conclude that rover-01 has never reported. The read knows;
   the console-wide check does not.

   WHY NOT A HOOK. These take no context and hold no state — they are
   predicates over a value the caller already has — so they are functions, and
   they can be used inside `useMemo`, inside a sort comparator, and in a
   `title` string, which a hook could not.
   ========================================================================== */

function readsOf(of: Read | readonly Read[]): readonly Read[] {
  return Array.isArray(of) ? (of as readonly Read[]) : [of as Read];
}

/**
 * True when not one of these sources has answered — so nothing derived from
 * them is a measurement, and a figure printed from them would be a claim
 * about the platform that nobody has read.
 *
 * Deliberately not "is it failing": a first read still in flight has not
 * answered either, and `0 registered` two seconds into a page load is the
 * same lie as `0 registered` four minutes into an outage. The panel's own
 * chrome — the fault line, the read tag, the 1px bar — is what tells the two
 * apart. The figure only has to refuse to invent one.
 */
export function unread(of: Read | readonly Read[]): boolean {
  const all = readsOf(of);
  return all.length > 0 && all.every((r) => !r.hasData);
}

/**
 * The value, or the em dash, for a prop that wants a plain value rather than
 * a node — `Panel`'s `count`, an `aria-label`, a `title`.
 */
export function figure(
  of: Read | readonly Read[],
  value: number | string | null | undefined,
): number | string {
  if (value === null || value === undefined || unread(of)) return NO_VALUE;
  return value;
}

/**
 * The one line a surface prints where its first row would be when the source
 * behind that row has not answered — the string half of what `useStanding`
 * gives the five instruments, for the handful of surfaces that draw their own
 * notice row (the signal stack's, the ledger's, the track's) and would
 * otherwise invent a sixth phrasing.
 *
 * TWO WORDS, AND THE DIFFERENCE MATTERS: a request in flight is a console
 * working on it, and a request that has failed is a console that has stopped.
 * The panel's 1px bar and `aria-busy` carry the same distinction; this is the
 * text channel of it, so no surface has to choose between "Loading…" and
 * "Failed to load" on its own.
 */
export function unreadLine(of: Read | readonly Read[]): string {
  const all = readsOf(of);
  /* `phase === 'first'` ALONE IS NOT "IN FLIGHT". A query the surface disabled
     — `useTelemetry(undefined)` while the roster it would pick a robot from is
     down — has no data, no error and no request, and it reports `first`
     forever. Printing `Reading…` over it is the console claiming to be working
     on something it is not going to do. `fetching` is the request itself, and
     it stays true across the whole retry ladder of a first read, so this does
     not flicker between backoffs. */
  const reading = all.some((r) => r.phase === 'first' && r.fetching);
  if (!reading) return 'Nothing has been read.';
  /* AND HOW LONG IT HAS BEEN READING. The same clause the fault line and the
     link banner carry, for the state before them — `Reading…` with no duration
     on it is the one line on a degraded surface that cannot be checked, and it
     is the line an operator sees for the whole of a hung request. Withheld
     under a second, where it would only ever read `0s` and flicker on a load
     that is behaving normally. */
  let waited: number | null = null;
  for (const r of all) {
    if (r.waitingMs === null) continue;
    waited = waited === null ? r.waitingMs : Math.max(waited, r.waitingMs);
  }
  return waited !== null && waited >= 1_000 ? `Reading… ${duration(waited)}` : 'Reading…';
}

/**
 * THE ONE OF THESE SOURCES WORTH NAMING.
 *
 * A figure counted across several endpoints is unread only when none of them
 * answered (see `unread`), but the chrome that reports it — a fault line, a
 * liveness tag — names ONE. The order is the operator's order of use: the
 * source that has actually failed, because that is the diagnosis; then one
 * that is still in flight, because "reading" is the honest word while a
 * request is out; then the first declared, which is the surface's own order of
 * dependence. Two dead endpoints get one line, about the one that failed.
 */
export function worstRead(of: Read | readonly Read[]): Read {
  const all = readsOf(of);
  return (
    all.find((r) => r.fault !== null)
    ?? all.find((r) => r.fetching)
    ?? (all[0] as Read)
  );
}

/**
 * Why a figure is an em dash, in the words the rest of the console uses. It
 * is a `title` rather than a visible note because the panel has already said
 * it once, on its fault line, in full — this is the answer to "why is THIS
 * one blank" for an operator who points at it.
 */
export function whyUnread(of: Read | readonly Read[]): string {
  const all = readsOf(of);
  const named = [...new Set(all.map((r) => r.path).filter((p): p is string => p !== null))];
  const where = named.length === 0 ? 'This source' : named.join(' · ');
  const failed = all.some((r) => r.fault !== null);
  return failed
    ? `${where} has not answered — this is not a zero`
    : `${where} has not answered yet — this is not a zero`;
}
